import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { AlertCircle, CheckCircle2, Clock, Loader2, Mail, Search, XCircle } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { db, formatDate } from "@/lib/billing";

type EmailLog = {
  id: string;
  template_id: string | null;
  invoice_id?: string | null;
  email_type: string;
  recipient_email: string;
  subject: string;
  status: string;
  provider_message_id?: string | null;
  error_message: string | null;
  metadata?: Record<string, unknown>;
  created_at: string;
};

type EmailTemplate = {
  id: string;
  name: string;
  template_key: string;
};

async function fetchLogs(): Promise<EmailLog[]> {
  const { data, error } = await db
    .from("email_logs")
    .select("id, template_id, invoice_id, email_type, recipient_email, subject, status, provider_message_id, error_message, metadata, created_at")
    .order("created_at", { ascending: false })
    .limit(250);
  if (error) throw error;
  return data ?? [];
}

async function fetchTemplates(): Promise<EmailTemplate[]> {
  const { data, error } = await db
    .from("email_templates")
    .select("id, name, template_key")
    .limit(500);
  if (error) throw error;
  return data ?? [];
}

const statusMeta = (status: string) => {
  if (status === "sent") return { icon: CheckCircle2, variant: "default" as const };
  if (status === "failed") return { icon: XCircle, variant: "destructive" as const };
  if (status === "queued") return { icon: Clock, variant: "secondary" as const };
  return { icon: AlertCircle, variant: "outline" as const };
};

export default function AdminEmailLogsPage() {
  const [search, setSearch] = useState("");
  const logsQuery = useQuery({ queryKey: ["email-logs"], queryFn: fetchLogs, refetchInterval: 30_000 });
  const templatesQuery = useQuery({ queryKey: ["email-log-templates"], queryFn: fetchTemplates });
  // Stable references for the empty-array fallbacks so the downstream
  // useMemo hooks below don't invalidate on every render.
  const logs = useMemo(() => logsQuery.data ?? [], [logsQuery.data]);
  const templates = useMemo(() => templatesQuery.data ?? [], [templatesQuery.data]);

  const templateMap = useMemo(
    () => Object.fromEntries(templates.map((template) => [template.id, template])),
    [templates],
  );

  const stats = useMemo(() => ({
    total: logs.length,
    sent: logs.filter((log) => log.status === "sent").length,
    failed: logs.filter((log) => log.status === "failed").length,
    queued: logs.filter((log) => log.status === "queued").length,
  }), [logs]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    if (!q) return logs;
    return logs.filter((log) => {
      const templateName = log.template_id ? templateMap[log.template_id]?.name : "";
      return [
        log.recipient_email,
        log.subject,
        log.email_type,
        log.status,
        log.error_message,
        log.provider_message_id,
        templateName,
      ].join(" ").toLowerCase().includes(q);
    });
  }, [logs, search, templateMap]);

  const isLoading = logsQuery.isLoading || templatesQuery.isLoading;
  const error = logsQuery.error || templatesQuery.error;

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-30 border-b border-border bg-background/80 px-6 py-4 backdrop-blur-xl">
        <h1 className="text-lg font-semibold text-foreground">Email Logs</h1>
        <p className="text-xs text-muted-foreground">Delivery status, recipients, templates, and provider errors.</p>
      </header>
      <main className="space-y-4 p-6">
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <StatCard label="Total" value={stats.total} />
          <StatCard label="Sent" value={stats.sent} tone="text-success" />
          <StatCard label="Queued" value={stats.queued} tone="text-warning" />
          <StatCard label="Failed" value={stats.failed} tone="text-destructive" />
        </div>

        <Card>
          <CardHeader className="gap-3">
            <CardTitle className="text-base">Recent email activity</CardTitle>
            <div className="relative max-w-md">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input className="pl-9" placeholder="Search email logs..." value={search} onChange={(event) => setSearch(event.target.value)} />
            </div>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="flex h-48 items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : error ? (
              <div className="rounded-md border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">
                {(error as Error).message || "Email logs could not be loaded."}
              </div>
            ) : filtered.length === 0 ? (
              <div className="flex h-48 flex-col items-center justify-center rounded-md border border-dashed border-border text-center">
                <Mail className="mb-2 h-8 w-8 text-muted-foreground" />
                <p className="text-sm font-medium text-foreground">{logs.length === 0 ? "No email logs yet" : "No logs match your search"}</p>
                <p className="mt-1 max-w-md text-xs text-muted-foreground">
                  Logs appear here when an email template test is queued or billing/account email workflows create delivery records.
                </p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Recipient</TableHead>
                    <TableHead>Template</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Subject</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Error</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((log) => {
                    const meta = statusMeta(log.status);
                    const Icon = meta.icon;
                    const template = log.template_id ? templateMap[log.template_id] : null;
                    return (
                      <TableRow key={log.id}>
                        <TableCell className="whitespace-nowrap">{formatDate(log.created_at)}</TableCell>
                        <TableCell>{log.recipient_email}</TableCell>
                        <TableCell className="max-w-[180px] truncate text-xs text-muted-foreground">
                          {template?.name || (log.template_id ? "Deleted template" : "-")}
                        </TableCell>
                        <TableCell>{log.email_type}</TableCell>
                        <TableCell className="max-w-[320px] truncate">{log.subject}</TableCell>
                        <TableCell>
                          <Badge variant={meta.variant} className="gap-1">
                            <Icon className="h-3 w-3" />
                            {log.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="max-w-[280px] truncate text-xs text-muted-foreground">{log.error_message ?? "-"}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}

function StatCard({ label, value, tone = "text-foreground" }: { label: string; value: number; tone?: string }) {
  return (
    <Card>
      <CardContent className="p-4">
        <p className={`text-2xl font-bold ${tone}`}>{value.toLocaleString()}</p>
        <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</p>
      </CardContent>
    </Card>
  );
}
